import clientRequest from '@/lib/client-request';
import { ReferenceItem } from '@/app/services/llm';
import type {
  ChatPublishedPost,
  ChatSnapshotDetail,
  PublishChatSnapshotReq,
  PublishChatSnapshotResp,
} from '@/app/services/chat';

export type DeepResearchStatus =
  | 'INIT'
  | 'RUNNING'
  | 'SUCCESS'
  | 'FAILED'
  | 'CANCELED';

export interface DeepResearchItem {
  uid: string;
  drid: string;
  status: DeepResearchStatus;
  point: number;
  user_query: string;
  sse_data: string[];
  error_msg: string;
}

export interface DeepResearchErrorResult {
  error_msg: string;
}

export interface DeepResearchFileUrl {
  file_url: string;
  mime_type:
    | 'text/markdown'
    | 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
}

export const typeMap = {
  // 'pre_search/plan': '制定研究计划',
  // 'pre_search/summarize_and_next_step': ['任务理解与思考', '总结内容与思考'],
  // 'pre_search/query_rewrite': '检索语句设计',
  // 'pre_search/search': '检索结果获取',
  // 'pre_search/end': '文献检索完成',
  'write/think_next_step': '下一步计划',
  'write/search_queries': '检索语句',
  'write/search': '文献结果',
  // 'write/plan': '制定研究计划',
  'write/plan_reasoning_append': '研究思考',
  'write/plan_append': '制定大纲',
} as const;

export type DeepResearchType =
  | keyof typeof typeMap
  | 'write/start'
  | 'write/write'
  | 'write/end'
  | 'write/file_ready'
  | 'custom_error'
  | 'retry_reload'
  | 'all_done';

export const getTypeLabel = (item: DeepResearchStreamMessageItem): string => {
  const defaultLabel = '';
  return typeMap[item.type as keyof typeof typeMap] || [defaultLabel];
};

export interface DeepResearchSummarizeResult {
  summary: { title: string; content: string }[];
  next_step: string;
}

export interface DeepResearchQueryResult {
  queries: string[];
}

export interface DeepResearchThinkResult {
  next_step: string;
  search_queries: string[];
}

export interface DeepResearchWriteResult {
  content: string;
  content_append: string;
  raw_content_append: string;
  ref_docs_append: ReferenceItem[];
}

export interface DeepResearchWriteEndResult {
  content: string;
  ref_docs: ReferenceItem[];
}

export type DeepResearchResult =
  | string
  | DeepResearchFileUrl
  | DeepResearchSummarizeResult
  | DeepResearchQueryResult
  | ReferenceItem[]
  | DeepResearchThinkResult
  | DeepResearchWriteResult
  | DeepResearchWriteEndResult
  | DeepResearchErrorResult;

export interface DeepResearchStreamMessageItem {
  type: DeepResearchType;
  code: number;
  message: string;
  version: string;
  log_id: string;
  seq: number;
  result: DeepResearchResult;
}

export function createDeepResearch(
  query: string,
  language: string,
  style?: string,
): Promise<DataResponse<DeepResearchItem>> {
  return clientRequest.post('/deep_research/create', {
    query,
    language,
    style,
  });
}

export type DeepResearchHistoryItemStatus = 'INIT' | 'WRITE' | 'DONE' | 'ERROR';

export interface DeepResearchHistoryItem {
  drid: string;
  error_msg: string;
  point: number;
  status: DeepResearchHistoryItemStatus;
  uid: string;
  user_query: string;
  ctime: string;
  share_id: string | null;
  share_to_square: boolean;
  snapshot_id?: string | null;
  share_token?: string | null;
  published_post?: ChatPublishedPost | null;
}

export function getDeepResearchHistory({
  offset,
  limit,
  keyword,
}: {
  offset: number;
  limit: number;
  keyword?: string;
}): Promise<PageInfo<DeepResearchHistoryItem>> {
  return clientRequest.post('/deep_research/history', {
    offset,
    limit,
    keyword,
  });
}

export function createDeepResearchShare(drid: string) {
  return clientRequest.post(`/deep_research/create_share/${drid}`);
}

export interface DeepResearchDetail {
  uid: string;
  drid: string;
  status: string;
  ctime: string;
  mtime: string;
  language: string;
  user_query: string;
  error_msg: string;
  markdown_url: string;
  docx_url: string;
  share_id: string;
  share_to_square: boolean;
  snapshot_id?: string | null;
  share_token?: string | null;
  published_post?: ChatPublishedPost | null;
}

export function getDeepResearchDetail(
  drid: string,
): Promise<DataResponse<DeepResearchDetail>> {
  return clientRequest.get(`/deep_research/${drid}/detail`);
}

export function createDeepResearchSnapshot(
  drid: string,
): Promise<DataResponse<ChatSnapshotDetail>> {
  return clientRequest.post(`/v3/deep-research/sessions/${drid}/snapshots`);
}

export function publishDeepResearchSession(
  drid: string,
  req?: PublishChatSnapshotReq,
): Promise<DataResponse<PublishChatSnapshotResp>> {
  return clientRequest.post(`/v3/deep-research/sessions/${drid}/publish`, req);
}

export function refreshDeepResearchSnapshot(
  snapshotId: string,
): Promise<DataResponse<ChatSnapshotDetail>> {
  return clientRequest.post(`/v3/deep-research/snapshots/${snapshotId}/refresh`);
}

export function publishDeepResearchSnapshot(
  snapshotId: string,
  req?: PublishChatSnapshotReq,
): Promise<DataResponse<PublishChatSnapshotResp>> {
  return clientRequest.post(`/v3/deep-research/snapshots/${snapshotId}/publish`, req);
}

export function unpublishDeepResearchSnapshot(
  snapshotId: string,
): Promise<DataResponse<PublishChatSnapshotResp>> {
  return clientRequest.post(`/v3/deep-research/snapshots/${snapshotId}/unpublish`);
}
